import React from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Collapse,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import type { MissionGroup } from '../../../../shared/onboarding/missionTypes';
import { coerceEventListingPlainText } from '../../../../shared/utils/coerceEventListingText';

export type ChecklistRow = {
  id: string;
  title: string;
  groupId: string;
  status: 'upcoming' | 'current' | 'done' | 'skipped';
};

interface MissionChecklistProps {
  groups: MissionGroup[];
  rows: ChecklistRow[];
}

export const MissionChecklist: React.FC<MissionChecklistProps> = ({
  groups,
  rows,
}) => {
  return (
    <Stack spacing={2}>
      {groups.map((group) => {
        const groupRows = rows.filter((row) => row.groupId === group.id);
        if (groupRows.length === 0) {
          return null;
        }
        return (
          <Box key={group.id}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              {coerceEventListingPlainText(group.title, 'Section')}
            </Typography>
            <Stack spacing={0.5}>
              {groupRows.map((row) => {
                const isCurrent = row.status === 'current';
                const isDone = row.status === 'done';
                return (
                  <Stack
                    key={row.id}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: isCurrent ? 'action.selected' : 'transparent',
                    }}
                  >
                    {isDone ? (
                      <CheckCircleOutlineIcon
                        color="success"
                        fontSize="small"
                        sx={{ mt: 0.25 }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        color={isCurrent ? 'primary' : 'disabled'}
                        fontSize="small"
                        sx={{ mt: 0.25 }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isCurrent ? 600 : 400,
                          textDecoration:
                            row.status === 'skipped' ? 'line-through' : 'none',
                        }}
                      >
                        {coerceEventListingPlainText(row.title, 'Step')}
                      </Typography>
                      <Collapse in={isCurrent}>
                        {isCurrent ? (
                          <Chip
                            size="small"
                            label="Current"
                            color="primary"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        ) : null}
                      </Collapse>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default MissionChecklist;
